import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";

/**
 * Accent color palettes. Each is the full 50→950 scale as "R G B" triplets,
 * matching Tailwind's named palettes so the UI keeps its shading depth.
 */
export const PALETTES = {
  teal: {
    label: "Teal",
    swatch: "#0d9488",
    shades: {
      50: "240 253 250", 100: "204 251 241", 200: "153 246 228", 300: "94 234 212",
      400: "45 212 191", 500: "20 184 166", 600: "13 148 136", 700: "15 118 110",
      800: "17 94 89", 900: "19 78 74", 950: "4 47 46",
    },
  },
  emerald: {
    label: "Green",
    swatch: "#059669",
    shades: {
      50: "236 253 245", 100: "209 250 229", 200: "167 243 208", 300: "110 231 183",
      400: "52 211 153", 500: "16 185 129", 600: "5 150 105", 700: "4 120 87",
      800: "6 95 70", 900: "6 78 59", 950: "2 44 34",
    },
  },
  blue: {
    label: "Blue",
    swatch: "#1e40af",
    shades: {
      50: "239 246 255", 100: "219 234 254", 200: "191 219 254", 300: "147 197 253",
      400: "96 165 250", 500: "59 130 246", 600: "37 99 235", 700: "30 64 175",
      800: "30 58 138", 900: "23 37 84", 950: "15 23 55",
    },
  },
  violet: {
    label: "Violet",
    swatch: "#7c3aed",
    shades: {
      50: "245 243 255", 100: "237 233 254", 200: "221 214 254", 300: "196 181 253",
      400: "167 139 250", 500: "139 92 246", 600: "124 58 237", 700: "109 40 217",
      800: "91 33 182", 900: "76 29 149", 950: "46 16 101",
    },
  },
  rose: {
    label: "Rose",
    swatch: "#e11d48",
    shades: {
      50: "255 241 242", 100: "255 228 230", 200: "254 205 211", 300: "253 164 175",
      400: "251 113 133", 500: "244 63 94", 600: "225 29 72", 700: "190 18 60",
      800: "159 18 57", 900: "136 19 55", 950: "76 5 25",
    },
  },
  amber: {
    label: "Amber",
    swatch: "#d97706",
    shades: {
      50: "255 251 235", 100: "254 243 199", 200: "253 230 138", 300: "252 211 77",
      400: "251 191 36", 500: "245 158 11", 600: "217 119 6", 700: "180 83 9",
      800: "146 64 14", 900: "120 53 15", 950: "69 26 3",
    },
  },
};

// Theme preferences are meant to outlive the browser, so they live in
// localStorage.
const STORAGE_KEY = "cortexqa-theme";

// Who is signed in is NOT a preference. The session cookie is what signs you
// in (AUTH-01; App.js asks GET users/me). The name and userkey are kept here in
// sessionStorage, for display only, so a refresh shows the app at once while
// the API is asked. It used to be localStorage, and once you had signed in on
// a machine the login page never showed again.
const SESSION_KEY = "cortexqa-session";

function applyPalette(colorKey) {
  const palette = PALETTES[colorKey] || PALETTES.teal;
  const root = document.documentElement;
  Object.entries(palette.shades).forEach(([shade, rgb]) => {
    root.style.setProperty(`--brand-${shade}`, rgb);
  });
}

// UI scale works as in the PlantOps web app (plantops-web-ui: layout/AppConfig.tsx
// and public/responsiveStyles.js), in two parts:
//
//  - Scale: the user's choice, one of seven sizes (10 … 22px, default 14,
//    − / + in steps of 2), saved in the user preference as settings.scale.
//  - Responsiveness: one factor from the window's size against the 1920 × 1080
//    design (the window clamped to 728–2560 × 400–1600, a wide window judged
//    by its height × 1.1), kept between 0.8 and 1.5 as PlantOps keeps its text.
//    So Full HD is 1, 2K about 1.3 and a 4K window 1.33 (it is clamped at 2560).
//
// This UI is drawn in Tailwind's 16px rem, so PlantOps' default 14 is our 16:
// the root font size is 16 × scale / 14 × factor, and every rem size follows.
export const SCALES = [10, 12, 14, 16, 18, 20, 22];
export const DEFAULT_SCALE = 14;

export function clampScale(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_SCALE;
  return SCALES.reduce((best, s) => (Math.abs(s - n) < Math.abs(best - n) ? s : best), SCALES[0]);
}

const BASE_WIDTH = 1920;
const BASE_HEIGHT = 1080;
const MIN_WIDTH = 728;
const MAX_WIDTH = 2560;
const MIN_HEIGHT = 400;
const MAX_HEIGHT = 1600;

export function screenFactor(
  width = typeof window !== "undefined" ? window.innerWidth : BASE_WIDTH,
  height = typeof window !== "undefined" ? window.innerHeight : BASE_HEIGHT
) {
  if (!width || !height) return 1;
  const w = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width));
  const h = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, height));
  const ws = w / BASE_WIDTH;
  const hs = h / BASE_HEIGHT;
  const main = w / h > BASE_WIDTH / BASE_HEIGHT ? Math.min(ws, hs * 1.1) : Math.min(ws, hs);
  return Math.min(1.5, Math.max(0.8, main));
}

export function rootPx(scale, factor = screenFactor()) {
  return Math.round(((16 * clampScale(scale)) / DEFAULT_SCALE) * factor * 100) / 100;
}

// Layout breakpoints follow the width the layout really has, in 16px units:
// a 2560px window at 21px is laid out like a 1950px one. Tailwind's sm: … 4xl:
// variants read these classes on <html> (tailwind.config.js), not the window.
export const BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280, "2xl": 1536, "3xl": 1920, "4xl": 2560 };

export function layoutWidth(px) {
  return typeof window === "undefined" ? 1280 : (window.innerWidth * 16) / px;
}

// The login page is a twelfth smaller on a 2K screen and up: there it read
// large next to the rest of the app.
const LOGIN_SMALLER_FROM = 1440;

function screenPx(scale, onLogin) {
  const px = rootPx(scale);
  const big = typeof window !== "undefined" && (window.screen?.height || 0) >= LOGIN_SMALLER_FROM;
  return onLogin && big ? Math.round((px * 11) / 12 * 100) / 100 : px;
}

function applyScale(scale, onLogin = false) {
  const px = screenPx(scale, onLogin);
  const root = document.documentElement;
  root.style.fontSize = `${px}px`;
  const width = layoutWidth(px);
  Object.entries(BREAKPOINTS).forEach(([name, min]) => {
    root.classList.toggle(`bp-${name}`, width >= min);
  });
  return { px, width };
}

function applyMode(mode) {
  document.documentElement.classList.toggle("dark", mode === "dark");
}

function readStored() {
  let theme = { mode: "light", color: "blue", scale: DEFAULT_SCALE };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Only the theme is taken from localStorage. Any identity left there by
      // an older build is ignored rather than restored, which is what logs a
      // returning user straight back in; the next write drops those fields.
      theme = {
        mode: parsed?.mode === "dark" ? "dark" : "light",
        color: parsed?.color || "blue",
        scale: clampScale(parsed?.scale ?? DEFAULT_SCALE),
      };
    }
  } catch {
    /* ignore */
  }

  let session = {};
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) session = JSON.parse(raw) || {};
  } catch {
    /* ignore */
  }

  return {
    ...theme,
    username: session.username,
    email: session.email,
    userkey: session.userkey,
    userid: session.userid,
  };
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [{ mode, color, scale, username, email, userkey, userid }, setState] = useState(readStored);

  useEffect(() => {
    applyMode(mode);
    applyPalette(color);
    try {
      // Theme only — writing identity here is what kept users signed in across
      // browser restarts. This also strips any identity an older build left.
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, color, scale }));
    } catch {
      /* ignore */
    }
    try {
      if (userkey) {
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ username, email, userkey, userid })
        );
      } else {
        // Signed out, so leave nothing behind for the next visitor.
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [mode, color, scale, username, email, userkey, userid]);

  // The root size and the breakpoint classes, before paint so nothing jumps.
  // A resize changes the size factor and the breakpoints, as in PlantOps.
  const [screen, setScreen] = useState(() => ({ px: rootPx(DEFAULT_SCALE), width: 0 }));
  const [onLogin, setOnLogin] = useState(false);
  useLayoutEffect(() => {
    const update = () => {
      const { width } = applyScale(scale, onLogin);
      setScreen({ px: rootPx(scale), width });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [scale, onLogin]);

  const value = {
    mode,
    color,
    scale,
    rootPx: screen.px,
    layoutWidth: screen.width,
    username,
    email,
    userkey,
    userid,
    palettes: PALETTES,
    setMode: (m) => setState((s) => ({ ...s, mode: m })),
    toggleMode: () => setState((s) => ({ ...s, mode: s.mode === "dark" ? "light" : "dark" })),
    setColor: (c) => setState((s) => ({ ...s, color: c })),
    setOnLogin,
    setScale: (v) => setState((s) => ({ ...s, scale: clampScale(v) })),
    setUsername: (u) => setState((s) => ({ ...s, username: u })),
    setEmail: (e) => setState((s) => ({ ...s, email: e })),
    setUserkey: (k) => setState((s) => ({ ...s, userkey: k })),
    setUserid: (u) => setState((s) => ({ ...s, userid: u })),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// The layout's width in 16px units (see BREAKPOINTS); outside a provider (a
// component test) the window's width.
export function useLayoutWidth() {
  const ctx = useContext(ThemeContext);
  if (ctx?.layoutWidth) return ctx.layoutWidth;
  return typeof window === "undefined" ? 1280 : window.innerWidth;
}

// The root font size in px (16 outside a provider), for sizes that have to be
// worked out in px, such as the script editor's code and its line markers.
export function useRemPx() {
  return useContext(ThemeContext)?.rootPx || 16;
}

// Login calls this while it is shown, for its smaller size; outside a provider
// (a component test) it does nothing.
export function useLoginScale() {
  const setOnLogin = useContext(ThemeContext)?.setOnLogin;
  useLayoutEffect(() => {
    if (!setOnLogin) return undefined;
    setOnLogin(true);
    return () => setOnLogin(false);
  }, [setOnLogin]);
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
